import { Calibration } from './calibration';
import { DeviceSetupStatus } from './device-setup-status';
import { LastRead } from './experiment-data';
import { ExperimentStatus } from './experiment-status';
import { Module } from './module-enum';
import { OxygenCalibration } from './oxygen-calibration';
import { PhCalibration } from './ph-calibration';
import { PaginationData } from './pagination-data.model';
import { SpargingCalibration } from './sparging-calibration';

export class Device {
  id: number;
  name: string;
  laboratoryId: number;
  macAddress?: string;
  hardwareVersion?: string;
  installedSoftwareVersion?: string;
  softwareWarning?: boolean | null;
  pumpTubeLength?: number;
  deviceStatusId?: DeviceSetupStatus;
  isOffline?: boolean;
  deviceModules?: Module[];
  isDeleted?: boolean;
  // Used to direct routing to the correct experiment from the device dashboard view when a device is running an experiment
  // and display experiment information on the device dashboard view
  experiment?: ExperimentSummary;
  // The ID of the current active OD calibration for device dashboard view routing
  odCalibrationId?: number;
  // The ID of either the oxygen calibration currently being used on an active experiment that the device is running
  // or of the current active oxygen calibration
  oxygenCalibrationId?: number;
  // The ID of either the ph calibration currently being used on an active experiment that the device is running
  phCalibrationId?: number;
  // The ID of either the sparging calibration currently being used on an active experiment that the device is running
  spargingCalibrationId?: number;
  // Used in the active experiment view to display the last read values for the device
  lastRead?: LastRead[];
  // Tracks the OD calibration currently being used on an active experiment that the device is running
  calibration?: Calibration;
  // Tracks the oxygen calibration currently being used on an active experiment that the device is running
  oxygenCalibration?: OxygenCalibration;
  // Tracks the pH calibration currently being used on an active experiment that the device is running
  phCalibration?: PhCalibration;
  spargingCalibration?: SpargingCalibration;
  isUpToDate?: boolean;
  latestSoftwareVersion?: string;
  isParticipating?: boolean;

  constructor(
    id: number,
    name: string,
    laboratoryId: number,
    macAddress?: string,
    hardwareVersion?: string,
    installedSoftwareVersion?: string,
    softwareWarning?: boolean | null,
  ) {
    this.id = id;
    this.name = name;
    this.laboratoryId = laboratoryId;
    this.macAddress = macAddress || null;
    this.hardwareVersion = hardwareVersion || null;
    this.installedSoftwareVersion = installedSoftwareVersion || null;
    this.softwareWarning = softwareWarning ?? null;
  }
}

export interface DeviceErrorLog {
  id: number;
  deviceId: number;
  errorText: string;
  detailedErrorText: string;
  viewed: boolean;
  createdBy: Date;
  createdAt: Date;
}

export interface ExperimentSummary {
  id: number;
  name: string;
  startTime: string;
  duration: number;
  experimentStatusId: ExperimentStatus;
}

export interface DeviceUpdateStatus {
  deviceStatusId: DeviceSetupStatus;
}

export interface DeviceUpdate {
  id: number;
  name: string;
  pumpTubeLength: number;
}

export class DeviceMessage {
  message: string;
  createdAt?: Date;

  constructor(message: string, createdAt?: Date) {
    this.message = message;
    this.createdAt = createdAt || null;
  }
}

export interface DeviceAdminSummary {
  id: number;
  name: string;
  macAddress?: string;
  deviceStatusId: DeviceSetupStatus;
  isDeleted: boolean;
  isOffline: boolean;
  installedModules: Module[];
  laboratoryId: number;
  laboratoryName: string;
  organisationId: number;
  organisationName: string;
  softwareWarning?: boolean | null;
}

export interface DeviceAdminIndex {
  devices: DeviceAdminSummary[];
  paginationData: PaginationData;
}